import { useEffect, useState, useCallback } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { urgenceApi, URGENCE_MOTIFS, type UrgenceResult } from '@/lib/urgenceApi'

// Fix leaflet default marker icons for vite bundlers
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl

// ── Default coordinates (Dakar) ──────────────────────────────

const DAKAR_LAT = 14.6937
const DAKAR_LON = -17.4441

// ── Verdict colors ──────────────────────────────────────────

const VERDICT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  RECOMMANDE: { bg: 'bg-green-100', text: 'text-green-800', label: 'RECOMMANDE' },
  PARTIEL: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'PARTIEL' },
  SATURE: { bg: 'bg-red-100', text: 'text-red-800', label: 'SATURE' },
}

// ── Custom map icons ─────────────────────────────────────────

const userIcon = L.divIcon({
  className: 'urgence-user-marker',
  html: `<div style="
    width: 18px; height: 18px; border-radius: 50%;
    background: #2563eb; border: 3px solid #fff;
    box-shadow: 0 0 0 2px #2563eb, 0 2px 8px rgba(37,99,235,0.5);
  "></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
})

function createVerdictIcon(rank: number, verdict: string): L.DivIcon {
  const colors: Record<string, string> = {
    RECOMMANDE: '#16a34a',
    PARTIEL: '#ea580c',
    SATURE: '#dc2626',
  }
  const bg = colors[verdict] || '#dc2626'
  return L.divIcon({
    className: 'urgence-structure-marker',
    html: `<div style="
      width: 32px; height: 32px; border-radius: 50%;
      background: ${bg}; color: #fff; border: 2px solid #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; font-family: 'Inter', system-ui, sans-serif;
    ">${rank}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })
}

// ── Map bounds fitter component ──────────────────────────────

interface FitBoundsProps {
  userLat: number
  userLon: number
  structures: UrgenceResult[]
}

function FitBounds({ userLat, userLon, structures }: FitBoundsProps) {
  const map = useMap()

  useEffect(() => {
    const points: L.LatLngTuple[] = [[userLat, userLon]]
    for (const s of structures) {
      if (s.latitude && s.longitude) points.push([s.latitude, s.longitude])
    }
    if (points.length > 1) {
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
    } else {
      map.setView([userLat, userLon], 13)
    }
  }, [map, userLat, userLon, structures])

  return null
}

// ── Main component ───────────────────────────────────────────

type Step = 'select' | 'loading' | 'results'

export default function UrgencePage() {
  const [step, setStep] = useState<Step>('select')
  const [selectedMotif, setSelectedMotif] = useState<string | null>(null)
  const [userLat, setUserLat] = useState(DAKAR_LAT)
  const [userLon, setUserLon] = useState(DAKAR_LON)
  const [results, setResults] = useState<UrgenceResult[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const doSearch = useCallback(async (motif: string, lat: number, lon: number) => {
    try {
      const data = await urgenceApi.search({ motif, latitude: lat, longitude: lon, limit: 3 })
      setResults(data)
      setStep('results')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur de connexion au serveur'
      setErrorMsg(msg)
      setStep('results')
    }
  }, [])

  const handleMotifTap = useCallback((motif: string) => {
    setSelectedMotif(motif)
    setErrorMsg(null)
    setResults([])
    setStep('loading')

    // Get geolocation and search simultaneously
    if (!navigator.geolocation) {
      doSearch(motif, DAKAR_LAT, DAKAR_LON)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude
        const lon = position.coords.longitude
        if (lat >= 12 && lat <= 17 && lon >= -17.6 && lon <= -11.4) {
          setUserLat(lat)
          setUserLon(lon)
          doSearch(motif, lat, lon)
        } else {
          doSearch(motif, DAKAR_LAT, DAKAR_LON)
        }
      },
      () => {
        doSearch(motif, DAKAR_LAT, DAKAR_LON)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [doSearch])

  const handleBack = () => {
    setStep('select')
    setSelectedMotif(null)
    setResults([])
    setErrorMsg(null)
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Emergency header */}
      <header className="bg-red-600 text-white px-4 py-3 shadow-md">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <h1 className="text-lg font-bold leading-tight">URGENCE 221</h1>
              <p className="text-red-100 text-xs">Trouvez le bon hopital, pas juste le plus proche</p>
            </div>
          </div>
          <a
            href="tel:1515"
            className="flex items-center gap-1.5 bg-white text-red-600 px-3 py-2 rounded-lg font-bold text-sm shadow-sm active:scale-95 transition-transform min-h-[44px]"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            1515
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* STEP 1: Symptom selector */}
        {step === 'select' && (
          <div className="max-w-lg mx-auto px-4 py-6">
            <h2 className="text-base font-semibold text-neutral-800 text-center mb-1">
              Quel est votre probleme ?
            </h2>
            <p className="text-xs text-neutral-500 text-center mb-5">
              Selectionnez votre situation pour trouver la structure adaptee
            </p>

            <div className="grid grid-cols-2 gap-3">
              {URGENCE_MOTIFS.map((motif) => (
                <button
                  key={motif.id}
                  onClick={() => handleMotifTap(motif.id)}
                  className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-transparent active:scale-95 transition-all min-h-[100px]"
                  style={{ backgroundColor: motif.bgColor, borderColor: motif.color + '30' }}
                >
                  <span className="text-3xl">{motif.icon}</span>
                  <span className="text-xs font-semibold text-center leading-tight" style={{ color: motif.color }}>
                    {motif.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: Loading */}
        {step === 'loading' && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm font-medium text-neutral-700 mb-1">{selectedMotif}</p>
              <p className="text-xs text-neutral-400">
                Localisation en cours... Recherche des structures...
              </p>
            </div>
          </div>
        )}

        {/* STEP 3: Results */}
        {step === 'results' && (
          <div className="max-w-lg mx-auto">
            {/* Back button */}
            <div className="px-4 pt-3 pb-2">
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-neutral-600 font-medium active:text-neutral-900"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Retour
              </button>
            </div>

            {/* Error */}
            {errorMsg && (
              <div className="px-4 pb-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-700 mb-2">{errorMsg}</p>
                  <button
                    onClick={() => selectedMotif && handleMotifTap(selectedMotif)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Reessayer
                  </button>
                </div>
              </div>
            )}

            {/* Map */}
            {results.length > 0 && (
              <>
                <div className="h-[220px] w-full">
                  <MapContainer
                    center={[userLat, userLon]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={false}
                    zoomControl={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds userLat={userLat} userLon={userLon} structures={results} />

                    <Marker position={[userLat, userLon]} icon={userIcon}>
                      <Popup><span className="text-sm font-medium">Votre position</span></Popup>
                    </Marker>

                    {results.map((s, i) => (
                      <Marker
                        key={s.code || i}
                        position={[s.latitude, s.longitude]}
                        icon={createVerdictIcon(i + 1, s.verdict)}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-bold">{s.name}</p>
                            <p className="text-neutral-500">{s.distanceKm} km</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>

                {/* Selected motif label */}
                <div className="px-4 pt-3 pb-1">
                  <p className="text-xs text-neutral-500">
                    Resultats pour <span className="font-semibold text-neutral-700">{selectedMotif}</span>
                  </p>
                </div>

                {/* Result cards */}
                <div className="px-4 py-3 space-y-3">
                  {results.map((s, i) => (
                    <ResultCard key={s.code || i} result={s} rank={i + 1} userLat={userLat} userLon={userLon} />
                  ))}
                </div>
              </>
            )}

            {results.length === 0 && !errorMsg && (
              <div className="text-center py-12 px-4">
                <p className="text-sm text-neutral-500">Aucune structure trouvee a proximite.</p>
              </div>
            )}

            {/* Footer */}
            <footer className="px-4 pb-6 pt-2 text-center">
              <p className="text-xs text-neutral-400">
                JoinXroad Sante &middot; Dakar, Senegal
              </p>
            </footer>
          </div>
        )}
      </main>
    </div>
  )
}

// ── Result card with verdict + capabilities ──────────────────

interface ResultCardProps {
  result: UrgenceResult
  rank: number
  userLat: number
  userLon: number
}

function ResultCard({ result, rank, userLat, userLon }: ResultCardProps) {
  const s = result
  const verdict = VERDICT_STYLES[s.verdict] || VERDICT_STYLES.SATURE
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLon}&destination=${s.latitude},${s.longitude}&travelmode=driving`

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div className="p-4">
        {/* Header: rank + name + verdict badge */}
        <div className="flex items-start gap-3">
          <span className="bg-neutral-800 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
            {rank}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-neutral-900 leading-tight">{s.name}</h3>
              <span className={`${verdict.bg} ${verdict.text} px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide`}>
                {verdict.label}
              </span>
            </div>
            <p className="text-sm text-neutral-500 mt-0.5">
              {s.distanceKm} km &middot; Score: {Math.round(s.totalScore)}/100
            </p>
          </div>
        </div>

        {/* Capability pills */}
        {s.capabilities && s.capabilities.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {s.capabilities.map((cap) => (
              <span
                key={cap.name}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  cap.available
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {cap.available ? (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {cap.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex border-t border-neutral-100">
        {s.phone ? (
          <a
            href={`tel:${s.phone}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-red-600 active:bg-red-50 transition-colors min-h-[48px] border-r border-neutral-100"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Appeler
          </a>
        ) : (
          <span className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-neutral-300 min-h-[48px] border-r border-neutral-100">
            Pas de tel.
          </span>
        )}
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-blue-600 active:bg-blue-50 transition-colors min-h-[48px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Itineraire
        </a>
      </div>
    </div>
  )
}
